import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';

const TicTacToeGame = () => {
  const { isDark } = useTheme();
  const [board, setBoard] = useState(Array(9).fill(null));
  const [isXNext, setIsXNext] = useState(true);
  const [scores, setScores] = useState({ X: 0, O: 0, draws: 0 });
  const [gameHistory, setGameHistory] = useState([]);

  const calculateWinner = (squares) => {
    const lines = [
      [0, 1, 2], [3, 4, 5], [6, 7, 8],
      [0, 3, 6], [1, 4, 7], [2, 5, 8],
      [0, 4, 8], [2, 4, 6]
    ];
    
    for (let i = 0; i < lines.length; i++) {
      const [a, b, c] = lines[i];
      if (squares[a] && squares[a] === squares[b] && squares[a] === squares[c]) {
        return squares[a];
      }
    }
    
    return squares.includes(null) ? null : 'Draw';
  };

  const handleClick = (i) => {
    if (calculateWinner(board) || board[i]) {
      return;
    }
    
    const newBoard = [...board];
    newBoard[i] = isXNext ? 'X' : 'O';
    setBoard(newBoard);
    setIsXNext(!isXNext);
  };

  const resetGame = () => {
    const winner = calculateWinner(board);
    
    if (winner && winner !== 'Draw') {
      setScores(prev => ({ ...prev, [winner]: prev[winner] + 1 }));
    } else if (winner === 'Draw') {
      setScores(prev => ({ ...prev, draws: prev.draws + 1 }));
    }
    
    if (winner) {
      setGameHistory(prev => [...prev, {
        winner,
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
    }
    
    setBoard(Array(9).fill(null));
    setIsXNext(true);
  };

  const winner = calculateWinner(board);

  // Theme Token Styles
  const titleClr   = isDark ? 'text-white' : 'text-gray-800';
  const textMuted  = isDark ? 'text-slate-400' : 'text-gray-500';
  const tileBg     = isDark ? 'bg-[#111C2C] hover:bg-[#1E3A5F] border-[#1E3A5F]' : 'bg-white hover:bg-gray-100 border-[#1E293B]';
  const historyBg  = isDark ? 'bg-[#111C2C] border-[#1E3A5F] text-slate-300' : 'bg-gray-100 border-[#1E293B] text-gray-700';

  const renderSquare = (i) => {
    return (
      <button
        className={`w-16 h-16 md:w-20 md:h-20 pixel-border text-2xl md:text-3xl font-bold flex items-center justify-center transition-colors ${tileBg}`}
        onClick={() => handleClick(i)}
      >
        <span className={board[i] === 'X' ? 'text-blue-500' : 'text-rose-500'}>
          {board[i]}
        </span>
      </button>
    );
  };

  return (
    <div className="max-w-md mx-auto">
      <h3 className={`text-xl md:text-2xl font-bold text-center mb-6 pixel-font ${titleClr}`}>
        ❌⭕ TIC TAC TOE
      </h3>
      
      {/* Scoreboard */}
      <div className="grid grid-cols-3 gap-2 mb-6 text-center bg-[var(--bg-muted)] p-3 pixel-border">
        <div>
          <div className={`text-[10px] pixel-font ${textMuted}`}>X WINS</div>
          <div className="text-xl font-bold text-blue-500">{scores.X}</div>
        </div>
        <div>
          <div className={`text-[10px] pixel-font ${textMuted}`}>DRAWS</div>
          <div className={`text-xl font-bold ${titleClr}`}>{scores.draws}</div>
        </div>
        <div>
          <div className={`text-[10px] pixel-font ${textMuted}`}>O WINS</div>
          <div className="text-xl font-bold text-rose-500">{scores.O}</div>
        </div>
      </div>

      {/* Game Status */}
      <div className="text-center mb-4">
        <div className={`text-sm md:text-base font-bold pixel-font ${
          winner === 'X' ? 'text-blue-500' : winner === 'O' ? 'text-rose-500' : titleClr
        }`}>
          {winner 
            ? winner === 'Draw' 
              ? 'SERI!' 
              : `PEMENANG: ${winner}`
            : `GILIRAN: ${isXNext ? 'X' : 'O'}`}
        </div>
      </div>

      {/* Game Board */}
      <div className="flex justify-center mb-6">
        <div className="grid grid-cols-3 gap-2">
          {Array(9).fill(null).map((_, idx) => renderSquare(idx))}
        </div>
      </div>

      {/* Action Button */}
      <div className="flex justify-center mb-6">
        <button 
          className="pixel-button bg-blue-600 text-white px-6 py-2.5 hover:bg-blue-700 text-xs md:text-sm font-bold pixel-font"
          onClick={resetGame}
        >
          {winner || !board.includes(null) ? 'MAIN LAGI' : 'RESET'}
        </button>
      </div>

      {/* Game History */}
      {gameHistory.length > 0 && (
        <div className="mt-6 border-t border-[var(--border-color)] pt-4">
          <h4 className={`text-xs pixel-font mb-3 ${textMuted}`}>RIWAYAT PERMAINAN:</h4>
          <div className="max-h-32 overflow-y-auto space-y-1.5 pr-1">
            {gameHistory.slice().reverse().map((game, index) => (
              <div key={index} className={`text-xs p-2 pixel-border flex justify-between ${historyBg}`}>
                <span>Game #{gameHistory.length - index}</span>
                <span className="font-bold">
                  {game.winner === 'Draw' ? 'Seri' : `Menang: ${game.winner}`}
                </span>
                <span className={textMuted}>{game.timestamp}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TicTacToeGame;